package it.unibo.tuprolog.solve.libs.oop.rules

import it.unibo.tuprolog.core.Scope
import it.unibo.tuprolog.core.Term
import it.unibo.tuprolog.solve.ExecutionContext
import it.unibo.tuprolog.solve.libs.oop.OOP.ACCESS_OPERATOR
import it.unibo.tuprolog.solve.rule.RuleWrapper

/**
 * The `.`/2 (`xfy`, priority 800) operator that lets Prolog code chain member accesses fluently,
 * e.g. `Obj.foo(1).bar` for "invoke `foo(1)` on `Obj`, then `bar` on the result".
 *
 * ```prolog
 * '.'(Ref, Method) :- fluent_reduce([Ref | Method], _).
 * ```
 *
 * As a clause in its own right, this lets a `.` expression also be used directly as a goal (e.g.
 * `X.println("hi")` as a whole query), invoking the chain purely for its side effects and
 * discarding the reduced result; capture the result instead with `:=` (see [ColonEquals]).
 *
 * This relies on `.`/2 being, by construction, the very same functor/arity Prolog uses for list
 * cons cells: `Obj.foo(1).bar` parses (right-associatively) into `.(Obj, .(foo(1), bar))`, which
 * is indistinguishable from the (improper) list `[Obj, foo(1) | bar]` -- so [FluentReduce] can
 * walk it exactly like a list of "things to invoke in sequence" without any extra parsing.
 *
 * @see FluentReduce
 */
object Dot : RuleWrapper<ExecutionContext>(ACCESS_OPERATOR, 2) {
    override val help: String =
        """
        `+Receiver.+Message`
        
        Fluent member access: invokes `Message` on the object, type, or `${'$'}Alias` referenced by `Receiver`, discarding the result. `Message` is an atom (a no-argument method, or a property to read) or a compound `name(Arg1, ..., ArgN)`; arguments are converted to objects, with `as` casts to steer overload selection. Since `.` is right-associative, accesses can be chained: `Obj.foo(1).bar` calls `foo(1)` on `Obj`, then `bar` on its result, each step via `invoke_method/3`. Used as a goal, it runs the chain for its side effects; to capture the final result write `Result := Obj.foo(1).bar` (see `:=/2`). Errors are those of `invoke_method/3`.

        **Examples**

        ```prolog
        ?- ${'$'}stdout.println(hello).
        % prints: hello
        yes.

        ?- new_object('java.util.ArrayList', [], L), L.add(a), L.add(b), list_items(L, Items).
        Items = [a, b].

        ?- new_object('java.util.ArrayList', [], L), L.nope.
        throws error(existence_error(_, _), _).
        ```
        """.trimIndent()

    private val Method by variables
    private val Ref by variables

    override val Scope.head: List<Term>
        get() =
            kotlin.collections.listOf(
                Ref,
                Method,
            )

    override val Scope.body: Term
        get() = structOf(FluentReduce.FUNCTOR, consOf(Ref, Method), `_`)
}
